import React, { useEffect, useRef, useState, useMemo } from 'react';
import { Select, Button, Spin } from 'antd';
import { useNavigate } from 'react-router-dom';
import * as echarts from 'echarts/core';
import { TooltipComponent, GridComponent, LegendComponent } from 'echarts/components';
import { BarChart } from 'echarts/charts';
import { CanvasRenderer } from 'echarts/renderers';
import SparklesIcon from 'components/AIChat/components/AiChatContent/imgs/sparkles.svg';
import useGetAppointmentTrends from 'hooks/useGetAppointmentTrends';
import useProviderHomeInfo from 'hooks/useProviderHomeInfo';
import dayjs from 'utils/dayjs';
import AIChat from 'components/AIChat';
import { EAIChatPageId } from 'types/ai';
import { PATH } from 'constants/path';
import s from './s.module.less';

echarts.use([TooltipComponent, GridComponent, LegendComponent, BarChart, CanvasRenderer]);

type TimeRange =
    | 'LAST_1_WEEK'
    | 'LAST_2_WEEKS'
    | 'LAST_4_WEEKS'
    | 'LAST_8_WEEKS'
    | 'LAST_12_WEEKS'
    | 'SINCE_YOU_STARTED';

interface AppointmentTrendsProps {
    onAskAgent?: () => void;
    onCloseBanner?: () => void;
}

// AIChat wrapper component to control show and close
const AIChatWrapper: React.FC<{ show: boolean; onClose: () => void; initialMessage?: string }> = ({
    show,
    onClose,
    initialMessage,
}) => {
    if (!show) return null;

    return (
        <div style={{ position: 'fixed', right: '24px', bottom: '24px', zIndex: 1000 }}>
            <AIChat
                key={`appointment-trends-ai-chat-${show}`}
                pageId={EAIChatPageId.PROVIDER_HOME_APPOINTMENT_TRENDS}
                defaultShow
                makeFix={false}
                initialMessage={initialMessage}
                onVisableChange={(visible) => {
                    if (!visible) {
                        onClose();
                    }
                }}
            />
        </div>
    );
};

const AppointmentTrends: React.FC<AppointmentTrendsProps> = ({ onAskAgent, onCloseBanner }) => {
    const navigate = useNavigate();
    const chartRef = useRef<HTMLDivElement>(null);
    const chartInstance = useRef<echarts.ECharts | null>(null);
    const [timeRange, setTimeRange] = useState<TimeRange>('LAST_4_WEEKS');
    const [showBanner, setShowBanner] = useState(true);
    const [showAIChat, setShowAIChat] = useState(false);
    const [homeInfo] = useProviderHomeInfo();
    const signUpDate = homeInfo?.signUpDate;

    // Calculate start and end dates based on time range
    const dateRange = useMemo(() => {
        const endDate = dayjs().endOf('day');
        let startDate: dayjs.Dayjs;

        if (timeRange === 'SINCE_YOU_STARTED') {
            // If "Since you started" is selected, use sign up date as start date
            if (signUpDate) {
                startDate = dayjs(signUpDate).startOf('day');
            } else {
                // If no sign up date, use a date far in the past (e.g., 1 year ago)
                startDate = endDate.subtract(365, 'day').startOf('day');
            }
        } else {
            // Calculate start date based on number of weeks
            let weeks: number;
            if (timeRange === 'LAST_1_WEEK') {
                weeks = 1;
            } else if (timeRange === 'LAST_2_WEEKS') {
                weeks = 2;
            } else if (timeRange === 'LAST_4_WEEKS') {
                weeks = 4;
            } else if (timeRange === 'LAST_8_WEEKS') {
                weeks = 8;
            } else {
                weeks = 12;
            }
            startDate = endDate.subtract(weeks * 7 - 1, 'day').startOf('day');
        }

        // Format as ISO format with timezone offset (e.g., 2025-11-03T14:35:20+08:00)
        return {
            start: startDate.format('YYYY-MM-DDTHH:mm:ssZ'),
            end: endDate.format('YYYY-MM-DDTHH:mm:ssZ'),
        };
    }, [timeRange, signUpDate]);

    // Get appointment trends data
    const [trendData, loading] = useGetAppointmentTrends(dateRange, true);

    // Process data: aggregate by day for LAST_1_WEEK, by month for LAST_12_WEEKS and SINCE_YOU_STARTED (if > 3 months), by week for others
    const chartData = useMemo(() => {
        const endDate = dayjs().endOf('day');
        let startDate: dayjs.Dayjs;
        let weeks: number | null = null;
        const isLast1Week = timeRange === 'LAST_1_WEEK';
        const isLast12Weeks = timeRange === 'LAST_12_WEEKS';
        const isSinceYouStarted = timeRange === 'SINCE_YOU_STARTED';
        const hasData = trendData && trendData.length > 0;

        if (isSinceYouStarted) {
            // For "Since you started", use sign up date as start
            if (signUpDate) {
                startDate = dayjs(signUpDate).startOf('day');
            } else {
                startDate = endDate.subtract(365, 'day').startOf('day');
            }
            // weeks will be null, meaning we'll show all periods from start to end
        } else {
            // Calculate weeks based on time range
            if (timeRange === 'LAST_1_WEEK') {
                weeks = 1;
            } else if (timeRange === 'LAST_2_WEEKS') {
                weeks = 2;
            } else if (timeRange === 'LAST_4_WEEKS') {
                weeks = 4;
            } else if (timeRange === 'LAST_8_WEEKS') {
                weeks = 8;
            } else {
                weeks = 12;
            }
            startDate = endDate.subtract(weeks * 7 - 1, 'day').startOf('day');
        }

        // Determine aggregation type based on time range
        // For LAST_12_WEEKS or SINCE_YOU_STARTED with > 3 months, use month aggregation
        const shouldAggregateByMonth = isLast12Weeks || (isSinceYouStarted && endDate.diff(startDate, 'month') > 3);

        // For LAST_1_WEEK, aggregate by day
        if (isLast1Week) {
            // Create a map from date to appointment count (aggregated by day)
            const dayDataMap = new Map<string, number>();

            if (hasData) {
                trendData.forEach((item) => {
                    const itemDate = dayjs(item.metricDate);
                    const dayKey = itemDate.format('YYYY-MM-DD');

                    // Accumulate appointment count for this day
                    const currentCount = dayDataMap.get(dayKey) || 0;
                    dayDataMap.set(dayKey, currentCount + item.bookingsCount);
                });
            }

            // Generate continuous day data
            const dates: string[] = [];
            const appointments: number[] = [];
            const weekRanges: string[] = [];

            let currentDate = startDate;

            while (currentDate.isBefore(endDate) || currentDate.isSame(endDate, 'day')) {
                const dayKey = currentDate.format('YYYY-MM-DD');

                dates.push(currentDate.format('MM/DD'));
                appointments.push(dayDataMap.get(dayKey) || 0);
                // Store date for tooltip
                weekRanges.push(currentDate.format('MM/DD/YYYY'));
                currentDate = currentDate.add(1, 'day');
            }

            return { dates, appointments, weekRanges, hasData };
        } else if (shouldAggregateByMonth) {
            // Create a map from month start date to appointment count (aggregated by month)
            const monthDataMap = new Map<string, number>();

            if (hasData) {
                trendData.forEach((item) => {
                    const itemDate = dayjs(item.metricDate);
                    // Get the start date of the month for this date
                    const monthStart = itemDate.startOf('month');
                    const monthKey = monthStart.format('YYYY-MM-DD');

                    // Accumulate appointment count for this month
                    const currentCount = monthDataMap.get(monthKey) || 0;
                    monthDataMap.set(monthKey, currentCount + item.bookingsCount);
                });
            }

            // Generate continuous month data
            const dates: string[] = [];
            const appointments: number[] = [];
            const weekRanges: string[] = [];

            // Start from the month containing the start date
            const startMonthStart = startDate.startOf('month');
            const endMonthStart = endDate.startOf('month');
            let currentMonthStart = startMonthStart;

            // Show all months from start to end
            while (currentMonthStart.isBefore(endMonthStart) || currentMonthStart.isSame(endMonthStart, 'month')) {
                const monthKey = currentMonthStart.format('YYYY-MM-DD');
                const monthEnd = currentMonthStart.endOf('month');

                dates.push(currentMonthStart.format('MMM YYYY'));
                appointments.push(monthDataMap.get(monthKey) || 0);
                // Store month start and end dates for tooltip
                weekRanges.push(`${currentMonthStart.format('MM/DD/YYYY')} - ${monthEnd.format('MM/DD/YYYY')}`);
                currentMonthStart = currentMonthStart.add(1, 'month');
            }

            return { dates, appointments, weekRanges, hasData };
        } else {
            // Create a map from week start date to appointment count (aggregated by week)
            const weekDataMap = new Map<string, number>();

            if (hasData) {
                trendData.forEach((item) => {
                    const itemDate = dayjs(item.metricDate);
                    // Get the start date of the week (Monday) for this date
                    const weekStart = itemDate.startOf('week');
                    const weekKey = weekStart.format('YYYY-MM-DD');

                    // Accumulate appointment count for this week
                    const currentCount = weekDataMap.get(weekKey) || 0;
                    weekDataMap.set(weekKey, currentCount + item.bookingsCount);
                });
            }

            // Generate continuous week data
            const dates: string[] = [];
            const appointments: number[] = [];
            const weekRanges: string[] = [];

            // Start from the week containing the start date
            const startWeekStart = startDate.startOf('week');
            const endWeekStart = endDate.startOf('week');
            let currentWeekStart = startWeekStart;

            // If weeks is specified, only show that many weeks from the end
            // Otherwise, show all weeks from start to end
            if (weeks !== null) {
                // Go back (weeks - 1) weeks from the end week to ensure we show exactly 'weeks' weeks
                currentWeekStart = endWeekStart.subtract(weeks - 1, 'week');
                const finalWeekStart = endWeekStart;

                while (currentWeekStart.isBefore(finalWeekStart) || currentWeekStart.isSame(finalWeekStart, 'day')) {
                    const weekKey = currentWeekStart.format('YYYY-MM-DD');
                    const weekEnd = currentWeekStart.endOf('week');

                    dates.push(currentWeekStart.format('MM/DD'));
                    appointments.push(weekDataMap.get(weekKey) || 0);
                    // Store week start and end dates for tooltip
                    weekRanges.push(`${currentWeekStart.format('MM/DD/YYYY')} - ${weekEnd.format('MM/DD/YYYY')}`);
                    currentWeekStart = currentWeekStart.add(1, 'week');
                }
            } else {
                // Show all weeks from start to end
                while (currentWeekStart.isBefore(endWeekStart) || currentWeekStart.isSame(endWeekStart, 'day')) {
                    const weekKey = currentWeekStart.format('YYYY-MM-DD');
                    const weekEnd = currentWeekStart.endOf('week');

                    dates.push(currentWeekStart.format('MM/DD'));
                    appointments.push(weekDataMap.get(weekKey) || 0);
                    // Store week start and end dates for tooltip
                    weekRanges.push(`${currentWeekStart.format('MM/DD/YYYY')} - ${weekEnd.format('MM/DD/YYYY')}`);
                    currentWeekStart = currentWeekStart.add(1, 'week');
                }
            }

            return { dates, appointments, weekRanges, hasData };
        }
    }, [trendData, timeRange, signUpDate]);

    useEffect(() => {
        if (!chartRef.current || loading) return;

        // Initialize chart
        if (!chartInstance.current) {
            chartInstance.current = echarts.init(chartRef.current);
        }

        const { dates, appointments, weekRanges, hasData } = chartData;

        // Always render chart to show axes, even if there's no data
        if (dates.length === 0) {
            return;
        }

        // Calculate Y-axis maximum (round up to nearest multiple of 5)
        // If no data, use default max of 40
        const maxValue = hasData ? Math.max(...appointments, 5) : 0;
        const yAxisMax = hasData ? Math.ceil(maxValue / 5) * 5 : 40;
        // Calculate integer interval (ensure it's an integer)
        const yAxisInterval = Math.ceil(yAxisMax / 8);

        const option = {
            tooltip: {
                trigger: 'axis',
                show: hasData, // Only show tooltip when there's data
                backgroundColor: '#ffffff',
                borderRadius: 8,
                borderWidth: 0,
                padding: 16,
                boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.10), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
                textStyle: {
                    color: '#111827',
                    fontSize: 16,
                    fontWeight: 400,
                    lineHeight: 24,
                },
                formatter: (params: Array<{ dataIndex: number; value: number }>) => {
                    if (!params || params.length === 0) return '';

                    const param = params[0];
                    const { dataIndex } = param;
                    const weekRange = weekRanges[dataIndex] || '';
                    const value = param.value || 0;

                    return `
                        <div style="display: flex; flex-direction: column; gap: 16px; width: 216px;">
                            <div style="font-size: 16px; font-weight: 400; line-height: 24px; color: #111827;">
                                ${weekRange}
                            </div>
                            <div style="display: flex; justify-content: space-between; align-items: center; font-size: 16px; font-weight: 500; line-height: 32px;">
                                <span style="color: #15191e;">Appointments</span>
                                <span style="color: #4b5563; font-size: 14px; line-height: 20px;">${value}</span>
                            </div>
                        </div>
                    `;
                },
            },
            grid: {
                left: 30,
                right: 46,
                top: 20,
                bottom: 50,
                containLabel: false,
            },
            xAxis: {
                type: 'category',
                data: dates,
                boundaryGap: true,
                axisLine: {
                    show: false,
                },
                axisTick: {
                    show: false,
                },
                axisLabel: {
                    color: '#6B7280',
                    fontSize: 14,
                    fontWeight: 400,
                    margin: 8,
                    overflow: 'truncate',
                },
            },
            yAxis: {
                type: 'value',
                min: 0,
                max: yAxisMax,
                interval: yAxisInterval,
                axisLine: {
                    show: false,
                },
                axisTick: {
                    show: false,
                },
                axisLabel: {
                    color: '#9CA3AF',
                    fontSize: 12,
                    fontWeight: 400,
                    formatter: (value: number) => {
                        // 确保显示为整数
                        return Math.round(value).toString();
                    },
                },
                splitLine: {
                    show: true,
                    lineStyle: {
                        color: '#E5E7EB',
                        type: 'solid',
                    },
                },
            },
            series: [
                {
                    name: 'Appointments',
                    type: 'bar',
                    data: appointments,
                    itemStyle: {
                        color: '#0E62F4',
                        borderRadius: [4, 4, 0, 0], // Top rounded corners
                    },
                    barWidth: '60%', // Bar chart width
                    // Hide series if no data
                    silent: !hasData,
                },
            ],
        };

        chartInstance.current.setOption(option, true);

        // Force resize after setting option to ensure correct dimensions
        // Use requestAnimationFrame to ensure DOM has been rendered
        requestAnimationFrame(() => {
            if (chartInstance.current) {
                chartInstance.current.resize();
            }
        });

        // Also use ResizeObserver to handle container size changes
        const resizeObserver = new ResizeObserver(() => {
            if (chartInstance.current) {
                chartInstance.current.resize();
            }
        });

        if (chartRef.current) {
            resizeObserver.observe(chartRef.current);
        }

        // Responsive adjustment
        const handleResize = () => {
            if (chartInstance.current) {
                chartInstance.current.resize();
            }
        };

        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
            resizeObserver.disconnect();
        };
    }, [timeRange, chartData, loading]);

    // Cleanup chart instance
    useEffect(() => {
        return () => {
            if (chartInstance.current) {
                chartInstance.current.dispose();
                chartInstance.current = null;
            }
        };
    }, []);

    const handleTimeRangeChange = (value: TimeRange) => {
        setTimeRange(value);
        // When time range changes, dateRange will be automatically recalculated via useMemo,
        // which will trigger the hook to refetch data
    };

    const handleAskAgent = () => {
        setShowAIChat(true);
        onAskAgent?.();
    };

    const handleCloseBanner = () => {
        setShowBanner(false);
        onCloseBanner?.();
    };

    const timeRangeOptions = [
        { label: 'Last 1 week', value: 'LAST_1_WEEK' },
        { label: 'Last 2 weeks', value: 'LAST_2_WEEKS' },
        { label: 'Last 4 weeks', value: 'LAST_4_WEEKS' },
        { label: 'Last 8 weeks', value: 'LAST_8_WEEKS' },
        { label: 'Last 12 weeks', value: 'LAST_12_WEEKS' },
        { label: 'Since you started', value: 'SINCE_YOU_STARTED' },
    ];

    return (
        <div className={s.wrapper}>
            <div className={s.header}>
                <h3 className={s.title}>Appointment trends</h3>
                <Select
                    value={timeRange}
                    onChange={handleTimeRangeChange}
                    className={s.timeRangeSelect}
                    suffixIcon={
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path
                                d="M5 7.5L10 12.5L15 7.5"
                                stroke="#374151"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        </svg>
                    }
                >
                    {timeRangeOptions.map((option) => (
                        <Select.Option key={option.value} value={option.value}>
                            {option.label}
                        </Select.Option>
                    ))}
                </Select>
            </div>

            {showBanner && (
                <div className={s.banner}>
                    <div className={s.bannerContent}>
                        <img src={SparklesIcon} alt="sparkles" className={s.sparklesIcon} />
                        <p className={s.bannerText}>
                            <span>Ask Klarity AI Agent to review your appointment trends and share ways to </span>
                            <span className={s.bannerTextBold}>grow</span>
                            <span> your bookings.</span>
                        </p>
                    </div>
                    <div className={s.bannerActions}>
                        <Button className={s.askAgentButton} onClick={handleAskAgent}>
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="18"
                                height="18"
                                viewBox="0 0 18 18"
                                fill="none"
                            >
                                <path
                                    fillRule="evenodd"
                                    clipRule="evenodd"
                                    d="M4.4998 1.7998C4.99686 1.7998 5.3998 2.20275 5.3998 2.6998V3.5998H6.2998C6.79686 3.5998 7.1998 4.00275 7.1998 4.4998C7.1998 4.99686 6.79686 5.3998 6.2998 5.3998H5.3998V6.2998C5.3998 6.79686 4.99686 7.1998 4.4998 7.1998C4.00275 7.1998 3.5998 6.79686 3.5998 6.2998V5.3998H2.6998C2.20275 5.3998 1.7998 4.99686 1.7998 4.4998C1.7998 4.00275 2.20275 3.5998 2.6998 3.5998H3.5998V2.6998C3.5998 2.20275 4.00275 1.7998 4.4998 1.7998ZM4.4998 10.7998C4.99686 10.7998 5.3998 11.2027 5.3998 11.6998V12.5998H6.2998C6.79686 12.5998 7.1998 13.0027 7.1998 13.4998C7.1998 13.9969 6.79686 14.3998 6.2998 14.3998H5.3998V15.2998C5.3998 15.7969 4.99686 16.1998 4.4998 16.1998C4.00275 16.1998 3.5998 15.7969 3.5998 15.2998V14.3998H2.6998C2.20275 14.3998 1.7998 13.9969 1.7998 13.4998C1.7998 13.0027 2.20275 12.5998 2.6998 12.5998H3.5998V11.6998C3.5998 11.2027 4.00275 10.7998 4.4998 10.7998Z"
                                    fill="white"
                                />
                                <path
                                    fillRule="evenodd"
                                    clipRule="evenodd"
                                    d="M10.7998 1.7998C11.2081 1.7998 11.5653 2.07474 11.6698 2.4695L12.7311 6.47884L15.7495 8.22024C16.0281 8.38097 16.1998 8.67816 16.1998 8.9998C16.1998 9.32145 16.0281 9.61864 15.7495 9.77937L12.7311 11.5208L11.6698 15.5301C11.5653 15.9249 11.2081 16.1998 10.7998 16.1998C10.3914 16.1998 10.0342 15.9249 9.92972 15.5301L8.86843 11.5208L5.85005 9.77937C5.57145 9.61864 5.3998 9.32144 5.3998 8.9998C5.3998 8.67816 5.57145 8.38097 5.85005 8.22024L8.86843 6.47885L9.92972 2.4695C10.0342 2.07474 10.3914 1.7998 10.7998 1.7998Z"
                                    fill="white"
                                />
                            </svg>
                            Ask Agent
                        </Button>
                        <button type="button" className={s.closeButton} onClick={handleCloseBanner}>
                            <svg
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    d="M18 6L6 18M6 6L18 18"
                                    stroke="#4B5563"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                        </button>
                    </div>
                </div>
            )}

            <Spin spinning={loading}>
                <div className={s.chartContainer}>
                    <div className={s.chartWrapper}>
                        <div className={s.legend}>
                            <div className={s.legendLine} />
                            <span className={s.legendText}>Appointments</span>
                        </div>
                        <div className={s.chartWrapperInner}>
                            <div ref={chartRef} className={s.chart} />
                            {!chartData.hasData && homeInfo?.proListing !== true && (
                                <div className={s.emptyState}>
                                    <div className={s.emptyStateContent}>
                                        <div className={s.emptyStateIcon}>
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                width="24"
                                                height="24"
                                                viewBox="0 0 24 24"
                                                fill="none"
                                            >
                                                <path
                                                    d="M21.9199 6.62C21.8185 6.37565 21.6243 6.18147 21.3799 6.08C21.2597 6.02876 21.1306 6.00158 20.9999 6H15.9999C15.7347 6 15.4804 6.10536 15.2928 6.29289C15.1053 6.48043 14.9999 6.73478 14.9999 7C14.9999 7.26522 15.1053 7.51957 15.2928 7.70711C15.4804 7.89464 15.7347 8 15.9999 8H18.5899L12.9999 13.59L9.70994 10.29C9.61698 10.1963 9.50637 10.1219 9.38452 10.0711C9.26266 10.0203 9.13195 9.9942 8.99994 9.9942C8.86793 9.9942 8.73722 10.0203 8.61536 10.0711C8.4935 10.1219 8.3829 10.1963 8.28994 10.29L2.28994 16.29C2.19621 16.383 2.12182 16.4936 2.07105 16.6154C2.02028 16.7373 1.99414 16.868 1.99414 17C1.99414 17.132 2.02028 17.2627 2.07105 17.3846C2.12182 17.5064 2.19621 17.617 2.28994 17.71C2.3829 17.8037 2.4935 17.8781 2.61536 17.9289C2.73722 17.9797 2.86793 18.0058 2.99994 18.0058C3.13195 18.0058 3.26266 17.9797 3.38452 17.9289C3.50637 17.8781 3.61698 17.8037 3.70994 17.71L8.99994 12.41L12.2899 15.71C12.3829 15.8037 12.4935 15.8781 12.6154 15.9289C12.7372 15.9797 12.8679 16.0058 12.9999 16.0058C13.132 16.0058 13.2627 15.9797 13.3845 15.9289C13.5064 15.8781 13.617 15.8037 13.7099 15.71L19.9999 9.41V12C19.9999 12.2652 20.1053 12.5196 20.2928 12.7071C20.4804 12.8946 20.7347 13 20.9999 13C21.2652 13 21.5195 12.8946 21.707 12.7071C21.8946 12.5196 21.9999 12.2652 21.9999 12V7C21.9984 6.86932 21.9712 6.74022 21.9199 6.62Z"
                                                    fill="#0E62F4"
                                                />
                                            </svg>
                                        </div>
                                        <p className={s.emptyStateText}>
                                            New providers often see slow activity during their first few days. More than{' '}
                                            <span className={s.emptyStateTextBold}>35% of Klarity providers</span> use
                                            Pro-Listings to boost visibility and secure their first bookings faster.
                                        </p>
                                        <Button
                                            type="primary"
                                            className={s.exploreButton}
                                            onClick={() =>
                                                navigate(`${PATH.DASHBOARD}/${PATH.MARKETING}?tab=prolisting`)
                                            }
                                        >
                                            Explore Pro-Listings
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </Spin>
            <AIChatWrapper
                show={showAIChat}
                onClose={() => setShowAIChat(false)}
                initialMessage="Ask AI Agent to analyze my appointment trends in the past week."
            />
        </div>
    );
};

export default AppointmentTrends;
